import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { Award, ShieldCheck, Users, Zap } from 'lucide-react'

import { cn } from '@/lib/utils'

type TrustItem = {
    title: string
    body: string
    icon: LucideIcon
}

const ITEMS: Array<TrustItem> = [
    {
        title: 'Trusted seller',
        body: 'Checked and listed by Bazio.',
        icon: Award
    },
    {
        title: 'Moderated listings',
        body: 'Listings are moderated by the community that published them.',
        icon: ShieldCheck
    },
    {
        title: 'Direct contact',
        body: 'You talk to the seller yourself on Telegram.',
        icon: Zap
    },
    {
        title: 'Community member',
        body: 'Part of a Telegram community with its own marketplace.',
        icon: Users
    }
]

interface SellerTrustBarProps {
    className?: string
}

/**
 * The closing trust strip. Four fixed statements about how Bazio itself works,
 * so nothing here claims anything about the individual seller: their own
 * checks live in the verification rail above and are database-driven.
 */
export const SellerTrustBar: FC<SellerTrustBarProps> = ({ className }): ReactNode => {
    return (
        <ul
            className={cn(
                'mta-border grid gap-8 rounded-mta-lg bg-paper p-6 sm:p-8 md:grid-cols-2 md:gap-x-0 lg:grid-cols-4',
                className
            )}
        >
            {ITEMS.map((item, index) => {
                const Icon = item.icon

                return (
                    <li
                        key={item.title}
                        className={cn(
                            'flex items-start gap-4 md:px-6',
                            index > 0 && 'lg:border-l lg:border-ink-900/15'
                        )}
                    >
                        <Icon className='size-8 shrink-0 text-brand-red-ink' strokeWidth={1.5} aria-hidden='true' />

                        <div className='min-w-0'>
                            <h3 className='text-base font-bold leading-snug text-ink-900'>
                                {item.title}
                            </h3>

                            <p className='mt-1.5 text-sm leading-relaxed text-text-muted'>
                                {item.body}
                            </p>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
